/**
 * projectManagementSpec — capability-level wizard spec for the white-label
 * ``project_management`` capability.
 *
 * Vendors today: Trello. Per CLAUDE.md "white-label naming OBBLIGATORIO"
 * (feedback_white_label_capability_naming.md) the capability slug + every
 * FE-facing string address ``project_management``; ``trello`` appears ONLY
 * as a vendor id inside ``vendors[]`` (mirrors ``crm`` → salesforce/hubspot).
 *
 * Flow (v1): single managed OAuth step — no BYOA, no manage surface beyond
 * connect/disconnect, so the connector falls through to the manifest
 * ``DEFAULT_ENTRY`` (no "Gestisci" button).
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { ListChecks, Search, Shield, SquareKanban, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface ProjectManagementConnection extends BaseConnection {
  readonly board_id?: string;
  readonly board_name?: string;
  readonly workspace_name?: string;
  readonly member_id?: string;
}

const iconCls = "h-4 w-4";

export const projectManagementSpec: CapabilityWizardSpec<ProjectManagementConnection> = {
  capability: "project_management",
  title: msg`Project management`,
  subtitle: msg`Board e attività di progetto, disponibili in chat`,
  icon: <SquareKanban className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "trello",
      label: "Trello",
      icon: <SquareKanban className={iconCls} aria-hidden />,
      description: msg`Board, liste e card sincronizzate via OAuth gestito`,
    },
  ],

  // ── Steps ────────────────────────────────────────────────────────────
  // Single managed OAuth step — the vendor picker lives in
  // ``<AddAccountMenu>`` (UI-side) and single-vendor capabilities skip it,
  // so the spec ships only the post-pick ``oauth`` step. ``spec.vendors``
  // stays the source of truth for which providers the picker shows.
  steps: [
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Verrai reindirizzato al provider per l'autorizzazione OAuth.`,
      oauth: { buttonLabel: msg`Autorizza il project management` },
    },
  ],

  benefits: [
    {
      icon: <Search className={iconCls} aria-hidden />,
      title: msg`Ricerca naturale`,
      body: msg`Chiedi ad Ando di trovare board, liste e card in linguaggio naturale.`,
    },
    {
      icon: <ListChecks className={iconCls} aria-hidden />,
      title: msg`Attività sempre a portata`,
      body: msg`Scadenze, assegnatari e stato delle card riassunti al volo.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Cross-referencing`,
      body: msg`Correla email, calendario e progetti in un'unica vista temporale.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Privato e sicuro`,
      body: msg`Token criptati at-rest, scope minimo, cancellazione GDPR garantita.`,
    },
  ],

  api: createCapabilityApi<ProjectManagementConnection>({ capability: "project_management" }),

  connectionKeyField: "id" as keyof ProjectManagementConnection,
  getDisplayName: (c) =>
    c.board_name ??
    c.workspace_name ??
    c.connection_key ??
    c.id ??
    i18nSingleton._(msg`Connessione`),
  getSecondaryId: (c) => c.board_id ?? c.member_id ?? null,
};
